<template>
  <v-row no-gutters class="mobile-panel" style="height: calc(100dvh - 70px)">
    <v-col cols="12" v-if="isFetched" class="fill-height">
      <v-row
        no-gutters
        style="height: 50px !important"
        class="pl-4 pr-2 mobile-panel-header flex-nowrap"
        align="center"
      >
        <v-col cols="auto" class="pr-2">
          <v-icon color="font" size="34" @click="$router.push({ name: 'main' })">mdi-chevron-left</v-icon>
        </v-col>
        <v-col class="text-body-2 font-weight-bold primary-color flex-shrink-1">
          <v-row no-gutters class="flex-nowrap">
            <v-col cols="auto" class="flex-shrink-1">
              <span class="font-size-16"> {{ offerNameValue }}</span>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="auto" class="pr-2">
          <v-tooltip v-model="copyTooltipVisible" bottom color="primary" attach style="position: relative">
            <template v-slot:activator="{ attrs }">
              <base-rounded-button
                icon="mdi-link"
                tooltip="tooltips.copyFeatureUrl"
                size="x-small"
                color="third"
                v-bind="attrs"
                @click="copyFeatureUrl"
              />
            </template>
            <span>{{ $i18n.t('tooltips.featureUrlCopied') }}</span>
          </v-tooltip>
        </v-col>
        <v-col cols="auto">
          <base-rounded-button
            icon="mdi-navigation"
            tooltip="tooltips.navigation"
            size="x-small"
            color="third"
            @click="openGoogleMaps()"
          />
        </v-col>
      </v-row>
      <v-row no-gutters style="max-height: 230px !important; height: 230px !important; overflow: hidden">
        <v-col cols="12">
          <sidebar-gallery class="mb-3" :lid="lid" :fid="fid" :image-attributes-values="imageAttributesValues" />
        </v-col>
      </v-row>
      <v-row no-gutters class="px-4 pt-3" style="height: 50px !important">
        <p class="d-flex align-center font-weight-medium text-body-2 mb-3">
          {{ $i18n.t('sidebar.area') }}:
          <span
            style="line-height: 1; border-radius: 13px"
            class="white--text font-weight-medium secondary px-3 py-1 ml-3"
          >
            {{ offerAreaValue }}
          </span>
        </p>
      </v-row>
      <v-row no-gutters style="height: calc(100vh - 400px); overflow-y: auto">
        <v-col cols="12">
          <sidebar-forms :attributes="currentAttributesForm" :values="currentAttributesValues" />
        </v-col>
      </v-row>
    </v-col>
    <main-loader v-else />
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import { GeoJSON } from 'ol/format';
import { transform } from 'ol/proj';

import BaseRoundedButton from '@/components/shared/buttons/BaseRoundedButton';
import MainLoader from '@/components/shared/MainLoader';
import SidebarGallery from '@/components/sidebar/SidebarGallery';
import SidebarForms from '@/components/sidebar/SidebarForms';

export default {
  name: 'TheMobileIdentification',
  components: { BaseRoundedButton, MainLoader, SidebarGallery, SidebarForms },
  computed: {
    brownfieldsLayers: get('admin/settings@BROWNFIELDS_LAYERS_SETTING.value.layers'),
    greenfieldsLayers: get('admin/settings@GREENFIELDS_LAYERS_SETTING.value.layers'),
    investedLayers: get('admin/settings@INVESTED_LAYERS_SETTING.value.layers'),

    offersDataSourceName: get('admin/settings@OFFERT_SETTING.value.datasource'),
    offersAreaAttributeName: get('admin/settings@OFFERT_SETTING.value.area_attribute'),
    offersAreaAttribute() {
      return this.dataSources[this.offersDataSourceName]?.attributes?.find(
        attribute => attribute.name === this.offersAreaAttributeName
      );
    },
    offerAreaValue() {
      return `${
        this.currentAttributesValues?.[this.offersAreaAttributeName]?.toFixed(2) || this.$i18n.t('default.noData')
      } ${this.offersAreaAttribute?.measurement_unit?.symbol || ''}`;
    },
    offersNameAttributeName: get('admin/settings@OFFERT_SETTING.value.name_attribute'),
    offerNameValue() {
      return this.currentAttributesValues?.[this.offersNameAttributeName] || '';
    },

    dataSources: get('featuresData/dataSources'),
    dataSourcesFeatures: get('featuresData/dataSourcesFeatures'),
    layers: get('featuresData/layers'),

    currentLng() {
      return this.$i18n.locale;
    },
    fid() {
      return +this.$route.params?.fid;
    },
    lid() {
      return +this.$route.params?.lid;
    },
    computedLidFid() {
      return { lid: this.lid, fid: this.fid };
    },
  },
  data: () => ({
    currentAttributesForm: undefined,
    currentAttributesValues: undefined,
    currentGeometry: undefined,
    isFetched: false,
    imageAttributesValues: [],
    copyTooltipVisible: false,
  }),
  methods: {
    getDataSourceFeature: call('featuresData/getDataSourceFeature'),
    async initFeature({ lid, fid } = this.computedLidFid) {
      for (const type of ['brownfields', 'greenfields', 'invested']) {
        const typeLayer = this.$_objectToArray(this[`${type}Layers`], 'key').find(layer => layer.value === this.lid);
        if (typeLayer) {
          if (typeLayer.key !== this.currentLng && this[`${type}Layers`][this.currentLng]) {
            this.$router.push({
              name: this.$route.name,
              params: { ...this.$route.params, lid: this[`${type}Layers`][this.currentLng] },
              query: this.$route.query,
            });
          }
          break;
        }
      }
      this.isFetched = false;
      const { dataSourceName, imageAttribute } = this.layers[lid];
      let feature;
      if (!this.dataSourcesFeatures[dataSourceName]?.[fid]) {
        await this.getDataSourceFeature({ dataSourceName, featureId: fid });
      }
      feature = this.dataSourcesFeatures[dataSourceName][fid];
      this.currentGeometry = { ...feature.geometry, crs: feature.crs };
      this.currentAttributesForm = this.$_getForm(
        this.layers[lid].attributes,
        this.dataSources[dataSourceName].attributes
      );
      this.currentAttributesValues = this.$_getValues(this.currentAttributesForm, feature.properties);
      const imgAttributes = this.dataSources[dataSourceName].attributes
        .filter(attribute => attribute.data_type?.name === 'hyperlink')
        .sort((a, b) => {
          if (a.name === imageAttribute) return false;
          const aGroupIndex = this.currentAttributesForm.findIndex(group =>
            group.elements.find(attribute => attribute.attribute === a.name)
          );
          const bGroupIndex = this.currentAttributesForm.findIndex(group =>
            group.elements.find(attribute => attribute.attribute === b.name)
          );
          if ((!aGroupIndex && aGroupIndex !== 0) || (!bGroupIndex && bGroupIndex !== 0)) {
            return false;
          } else if (
            (aGroupIndex || aGroupIndex === 0) &&
            (bGroupIndex || bGroupIndex === 0) &&
            aGroupIndex !== bGroupIndex
          ) {
            return aGroupIndex - bGroupIndex;
          } else if (
            (aGroupIndex || aGroupIndex === 0) &&
            (bGroupIndex || bGroupIndex === 0) &&
            aGroupIndex === bGroupIndex
          ) {
            const aIndex = this.currentAttributesForm[aGroupIndex]?.elements.findIndex(
              attribute => attribute.attribute === a.name
            );
            const bIndex = this.currentAttributesForm[bGroupIndex]?.elements.findIndex(
              attribute => attribute.attribute === b.name
            );
            return aIndex - bIndex;
          }
          return false;
        })
        .map(attribute => attribute.name);
      this.imageAttributesValues = imgAttributes
        .map(attribute => {
          const attrValue = feature.properties[attribute];
          return attrValue?.startsWith('/') ? `${process.env.VUE_APP_API_URL}${attrValue}` : attrValue;
        })
        .filter(img => !!img);
      this.isFetched = true;
    },
    openGoogleMaps(geometry = this.currentGeometry) {
      const olGeometry = new GeoJSON().readGeometry(geometry, {
        dataProjection: this.$_config.mapDataCoordinateSystem?.epsg,
        featureProjection: this.$_config.mapDataCoordinateSystem?.epsg,
      });
      const centerCoords = this.$_getGeometryCentroid(olGeometry);
      const [x, y] = transform(centerCoords, this.$_config.mapDataCoordinateSystem?.epsg, 'EPSG:4326');
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${y},${x}`);
    },
    copyFeatureUrl() {
      navigator.clipboard.writeText(window.location.href);
      this.copyTooltipVisible = true;
      setTimeout(() => {
        this.copyTooltipVisible = false;
      }, 1500);
    },
  },
  watch: {
    computedLidFid: {
      handler(nV) {
        this.initFeature(nV);
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
